import { useAuthStore } from '@/stores/authStore';
import { selectGrantsStatus, type GrantKey } from '@/stores/grants';

/**
 * Layout state for a grant-gated region, so a control keeps its place while
 * the session confirms access instead of popping in or vanishing under the
 * pointer:
 *
 * - `ready`   — confirmed granted; render the control normally.
 * - `pending` — a grant restored from storage is not confirmed yet (or its
 *   read failed); render the same control disabled, never actionable.
 * - `hidden`  — not granted, or nothing suggests it will be. A fresh login has
 *   no restored grant, so a granted region still appears once when confirmed.
 *
 * Authority is `use*Access` (confirmed only); this hook only decides layout.
 */
export type GrantSlot = 'ready' | 'pending' | 'hidden';

export function useGrantSlot(key: GrantKey): GrantSlot {
  return useAuthStore((s) => {
    if (s.user?.[key] !== true) return 'hidden';
    return selectGrantsStatus(s) === 'ready' ? 'ready' : 'pending';
  });
}

/** Tooltip/reason for a `pending` slot. */
export function useGrantPendingReason(): string {
  return useAuthStore((s) =>
    selectGrantsStatus(s) === 'error'
      ? "Couldn't confirm access — retries when you return to this tab"
      : 'Confirming access…');
}
